import { inject, Injectable, Injector, Signal, signal } from '@angular/core';
import { EMPTY, from, of, switchMap, tap, timer } from 'rxjs';
import { FirebaseAuthService } from './firebase-auth.service';

/**
 * Ends a developer login session (see `dev-login.initializer.ts`) after its hard 1 h limit: a
 * logout at the ID token's `devLoginExpiresAt`. Regular sessions carry no such claim and are
 * never touched.
 */
@Injectable({ providedIn: 'root' })
export class DevLoginExpiryService {
  private readonly firebaseAuth = inject(FirebaseAuthService);
  // LogoutService is loaded and resolved only when a dev session actually ends: it injects every
  // person-scoped state service — statically imported they would all land in the initial bundle, and
  // constructing AuthSessionService among them would start the login GET at app startup.
  private readonly injector = inject(Injector);

  private readonly _isDevSession = signal(false);
  /** Drives the "Support-Sitzung" banner (AppComponent). */
  public readonly isDevSession: Signal<boolean> = this._isDevSession.asReadonly();

  /** Watches every signed-in user; a user change or sign-out cancels the pending logout. */
  public start(): void {
    this.firebaseAuth.currentUser$
      .pipe(
        switchMap((user) => (user ? from(this.firebaseAuth.getDevLoginExpiresAtAsync()) : of(null))),
        tap((expiresAt) => this._isDevSession.set(expiresAt !== null)),
        switchMap((expiresAt) => (expiresAt === null ? EMPTY : timer(new Date(expiresAt * 1000))))
      )
      .subscribe(() => void this.endSessionAsync());
  }

  private async endSessionAsync(): Promise<void> {
    const { LogoutService } = await import('./logout.service');
    await this.injector.get(LogoutService).logoutAsync();
  }
}
